import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, Clock, Save, Loader2 } from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useToast } from '../../contexts/ToastContext';
import { format, parse } from 'date-fns';

interface Game {
  id: string;
  titulo: string;
  data?: string;
  local?: string | null;
  public_token: string;
  times?: any;
}

interface EditGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: Game | null;
  onGameUpdated?: (game: Game) => void;
}

const EditGameModal: React.FC<EditGameModalProps> = ({
  isOpen,
  onClose,
  game,
  onGameUpdated
}) => {
  const { supabase } = useSupabase();
  const { showSuccess, showError } = useToast();
  
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Função para fazer parsing correto da data/hora
  const parseGameDateTime = (dateTimeString: string) => {
    try {
      // Primeiro tentar como ISO string
      const isoDate = new Date(dateTimeString);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
      
      // Se falhar, tentar parse manual
      if (dateTimeString.includes(' ')) {
        return parse(dateTimeString, 'yyyy-MM-dd HH:mm:ss', new Date());
      }
      
      // Fallback para apenas data
      return parse(dateTimeString, 'yyyy-MM-dd', new Date());
    } catch (error) {
      console.error('Erro ao fazer parse da data:', error);
      return new Date(dateTimeString); // Fallback
    }
  };

  useEffect(() => {
    if (game && isOpen) {
      setTitle(game.titulo);
      setLocation(game.local || '');
      
      // Parse da data e hora do jogo
      const gameDateTime = parseGameDateTime(game.data);
      setDate(format(gameDateTime, 'yyyy-MM-dd'));
      setTime(format(gameDateTime, 'HH:mm'));
    }
  }, [game, isOpen]);

  const resetForm = () => {
    setTitle('');
    setDate('');
    setTime('');
    setLocation('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!game) {
      setError('Dados do jogo não encontrados');
      return;
    }

    if (!title || !date || !time) {
      setError('Título, data e horário são obrigatórios');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Combinar data e hora no formato correto
      const combinedDateTime = `${date}T${time}:00`;
      
      const { data: updatedGame, error: updateError } = await supabase
        .from('jogos')
        .update({
          titulo: title,
          data: combinedDateTime,
          local: location || null
        })
        .eq('id', game.id)
        .select()
        .single();

      if (updateError) throw updateError;

      showSuccess(
        'Jogo atualizado!',
        'As informações do jogo foram atualizadas com sucesso'
      );

      // Chamar callback se fornecido
      if (onGameUpdated && updatedGame) {
        onGameUpdated(updatedGame);
      }

      handleClose();
    } catch (err: any) {
      console.error('Erro ao atualizar jogo:', err);
      setError(err.message || 'Erro ao atualizar jogo');
      showError(
        'Erro ao atualizar jogo',
        err.message || 'Ocorreu um erro inesperado'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !game) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
          <h2 className="text-xl font-bold">Editar Jogo</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="editTitle" className="block text-sm font-medium mb-1">
                  Título do Jogo *
                </label>
                <input
                  type="text"
                  id="editTitle"
                  className="input w-full"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Pelada de Sábado"
                  required
                />
              </div>

              <div>
                <label htmlFor="editDate" className="block text-sm font-medium mb-1">
                  Data *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    id="editDate"
                    className="input w-full pl-10"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="editTime" className="block text-sm font-medium mb-1">
                  Horário *
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="time"
                    id="editTime"
                    className="input w-full pl-10"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="editLocation" className="block text-sm font-medium mb-1">
                  Local
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    id="editLocation"
                    className="input w-full pl-10"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Ex: Campo do Clube"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={handleClose}
                className="btn-outline"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || !title || !date || !time}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Salvando...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    <span>Salvar Alterações</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditGameModal;